import { computed, toValue } from 'vue';
import { format, isValid } from 'date-fns';
import { tz } from '@date-fns/tz';

const berlinTz = tz('Europe/Berlin');
const EMPTY_DISPLAY = '–';

function parseBookingDate(value) {
  if (!value) {
    return new Date(NaN);
  }

  const date = new Date(value);
  return isValid(date) ? date : new Date(NaN);
}

function roundOneDecimal(value) {
  return Math.round(value * 10) / 10;
}

function formatAverage(value) {
  if (value === null) {
    return EMPTY_DISPLAY;
  }

  return Number(value).toFixed(1);
}

export function useUserBookingMetrics(bookingsSource) {
  const activeBookings = computed(() => {
    const bookings = toValue(bookingsSource);
    if (!Array.isArray(bookings)) {
      return [];
    }

    return bookings.filter((booking) => !booking?.cancelledAt);
  });

  const averageDurationMinutes = computed(() => {
    const bookings = activeBookings.value;
    if (bookings.length === 0) {
      return null;
    }

    let totalMinutes = 0;
    let counted = 0;

    bookings.forEach((booking) => {
      const start = parseBookingDate(booking?.startTime);
      const end = parseBookingDate(booking?.endTime);
      if (!isValid(start) || !isValid(end)) {
        return;
      }

      const minutes = (end.getTime() - start.getTime()) / 60000;
      if (minutes < 0) {
        return;
      }

      totalMinutes += minutes;
      counted += 1;
    });

    if (counted === 0) {
      return null;
    }

    return roundOneDecimal(totalMinutes / counted);
  });

  const bookingsPerActiveDay = computed(() => {
    const bookings = activeBookings.value;
    if (bookings.length === 0) {
      return null;
    }

    const activeDays = new Set();
    let counted = 0;

    bookings.forEach((booking) => {
      const start = parseBookingDate(booking?.startTime);
      if (!isValid(start)) {
        return;
      }

      activeDays.add(format(start, 'yyyy-MM-dd', { in: berlinTz }));
      counted += 1;
    });

    if (activeDays.size === 0) {
      return null;
    }

    return roundOneDecimal(counted / activeDays.size);
  });

  const formattedAverageDuration = computed(() => formatAverage(averageDurationMinutes.value));
  const formattedBookingsPerActiveDay = computed(() => formatAverage(bookingsPerActiveDay.value));
  const hasDurationMetric = computed(() => averageDurationMinutes.value !== null);
  const hasBookingsPerDayMetric = computed(() => bookingsPerActiveDay.value !== null);

  return {
    formattedAverageDuration,
    formattedBookingsPerActiveDay,
    hasDurationMetric,
    hasBookingsPerDayMetric,
  };
}
